import { useCallback, useEffect } from "react";

import { QuestionPreviewNext } from "@vericus/cadmus-common";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppSelector } from "@/data/hooks";
import { selectExamBacktrackDisabled } from "@/features/assignment";
import { useMarkerRubricOpen } from "@/features/rubric";
import {
  AnswerBlockFragment,
  QuestionType,
  useUpdateAnswerBlockMutation,
} from "@/generated/graphql";
import { startSpan } from "@/lib/activity-span";

import { mapQuestionType } from "../../utils/map-graphql-to-json-schema-types";
import * as styles from "./answer-form.css";
import { PromptPreview } from "./prompt-preview";
import { ResponseForm } from "./response-form";

interface Props {
  /** Answer block modified in this form */
  answerBlock: AnswerBlockFragment;
  /** References answer block modified in this form */
  referencesAnswerBlock: AnswerBlockFragment | null;
  /** Make the answer form readonly */
  isReadonly?: boolean;
  /** Parent answer whose prompt is optionally rendered at the top. */
  parentAnswerBlockId?: string;
}

/**
 * Answer form area for a question with prompt and response input areas.
 */
export function AnswerForm(props: Props) {
  const {
    answerBlock,
    referencesAnswerBlock,
    isReadonly,
    parentAnswerBlockId,
  } = props;

  const { track } = useJitsu();

  const examBacktrackDisabled = useAppSelector(selectExamBacktrackDisabled);

  const { id: answerBlockId, question } = answerBlock;

  const [updateAnswerBlock] = useUpdateAnswerBlockMutation();
  const onUpdateBookmark = useCallback(
    (bookmark: boolean) => {
      updateAnswerBlock({
        variables: {
          answerBlockId,
          input: {
            skip: null,
            bookmark,
            confirm: null,
          },
        },
      });
      if (bookmark) {
        track(JitsuEvent.QUESTION_BOOKMARKED, {
          question_id: question?.id,
          question_type: question?.questionType,
          answer_block_id: answerBlockId,
        });
      }
    },
    [updateAnswerBlock, answerBlockId, question, track]
  );

  const openMarkerRubric = useMarkerRubricOpen({
    questionId: answerBlock.question?.id,
  });

  // We try to track how much time the student spend answering one question. To
  // achieve this we can track when the question is opened and then track when
  // the question is skipped/answered. The difference between those two time
  // marks could be something meaningful for analytics.
  useEffect(() => {
    // The student is now under the ANSWER span.
    startSpan({
      type: "answer",
      answerBlockId,
    });

    if (question?.id) {
      track(JitsuEvent.QUESTION_OPENED, {
        question_id: question.id,
        question_type: question.questionType,
        answer_block_id: answerBlockId,
      });
    }
  }, [track, question?.id, answerBlockId, question?.questionType]);

  if (!question) {
    return null;
  }

  const questionTitle =
    question.questionType === QuestionType.Section
      ? `Part ${answerBlock.orderLabel}`
      : `Question ${answerBlock.orderLabel}`;
  const promptDoc = question?.body?.promptDoc ?? null;

  // If no backtracking, no point bookmarking
  const withBookmark =
    question.questionType !== QuestionType.Section && !examBacktrackDisabled;

  return (
    <div className={styles.root}>
      {parentAnswerBlockId && (
        <PromptPreview answerBlockId={parentAnswerBlockId} />
      )}
      <QuestionPreviewNext
        mode="question"
        questionId={question.id}
        promptDoc={promptDoc}
        questionTitle={questionTitle}
        withBookmark={withBookmark}
        onUpdateBookmark={onUpdateBookmark}
        bookmarked={answerBlock.bookmark ?? undefined}
        data={{
          max_score: answerBlock.points ?? null,
          question_type: mapQuestionType(question.questionType),
          answer: null,
          references_answer: null,
          fields: null,
          field_outcomes: null,
          question_outcome: null,
          prompt_image: question.body?.promptImage ?? null,
        }}
        onRubricClick={openMarkerRubric}
      />
      {question.body?.fields && question.body.fields.length > 0 && (
        <ResponseForm
          key={answerBlockId}
          questionType={question.questionType}
          answerBlockId={answerBlockId}
          referencesAnswerBlockId={referencesAnswerBlock?.id ?? null}
          fields={question.body?.fields}
          isReadonly={isReadonly}
          shuffle={answerBlock.shuffle ?? undefined}
        />
      )}
    </div>
  );
}
